import {isNil} from "lodash";
import moment from "moment";
import EventTypes from "@wesstron/utils/Api/constants/eventTypes";
import {
    editSowCycle,
    editSowCycleNotificationFailure,
    editSowCycleNotificationSuccess,
} from "../../../api/cycles/editSowCycle";
import {GraftingProgramApplicationTypes} from "../../../constans/graftingProgramApplicationTypes";
import {GRAFTING_RESULT} from "../../../constans/graftingResults";
import animalsDB from "../../../database/animalsDB";
import {
    getMedicineArrayForGroup,
    getMedicineArrayForSelectedAnimal,
    getSelectedAnimalForDocuments,
    makeGetAnimalsInGroup,
} from "../../../selectors/animalDocumentsSelectors";
import {getEmployeeList, getServiceList} from "../../../selectors/userSelector";
import {checkIfUserHasPrivilegedAccess} from "../../../utils/NewRolesUtils";
import {employeesValueFormatter} from "../../../utils/RaportsUtils";
// import {getManageSubgroups} from "../../../utils/SettingsUtils";
import {useCallback, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {show} from "redux-modal";
import {isMobile} from "../../../utils/MobileUtils";
import {sortDateStrings} from "../../../utils/SortUtils";
import {formatLocationName} from "../../../utils/global-formatters/formatLocationName";
import TableGrid from "../../basics/table-grid/TableGrid";
import DefaultMobileRow from "../../basics/table-grid/default-mobile-row/DefaultMobileRow";
import {ModalName as ConfirmModalName} from "../../modals-new/confirm-modal/ConfirmModal";
import {ModalName as EditGraftingModalName} from "../../modals-new/edit-grafting-modal/EditGraftingModal";
import {ModalName as EditTreatmentModalName} from "../../modals-new/edit-treatment-modal/EditTreatmentModal";
import CollapsableContainer from "../containers/CollapsableContainer";
import DateHeaderComponent from "./DateHeaderComponent";
import DoneHeaderComponent from "./DoneHeaderComponent";
import {useTranslation} from "react-i18next";
import {treatmentTranslator} from "../../../utils/GraftingProgramUtils";

function GraftingProgramGridCardWrapper({showCard, defaultExpanded, children}) {
    const {t} = useTranslation();

    if (showCard) {
        return (
            <CollapsableContainer.Card
                id="cy-grafting-card"
                header={t("graftingProgramGridCardTitle")}
                defaultExpanded={defaultExpanded}>
                {children}
            </CollapsableContainer.Card>
        );
    }
    return (
        <CollapsableContainer.Div
            header={t("graftingProgramGridCardTitle")}
            defaultExpanded={defaultExpanded}>
            {children}
        </CollapsableContainer.Div>
    );
}

function IconComponent({value}) {
    const icon = useMemo(() => {
        switch (value) {
            case GRAFTING_RESULT.NOT_DONE:
                return "fas fa-fw fa-times error";
            case GRAFTING_RESULT.DONE:
                return "fas fa-fw fa-check success";
            case GRAFTING_RESULT.PENDING:
                return "fas fa-fw fa-clock info";
            default:
                return "";
        }
    }, [value]);

    return <i className={icon} />;
}

function EditComponent({object}) {
    const dispatch = useDispatch();

    const onClick = useCallback(() => {
        if (object.event.EvCode === EventTypes.TREATMENT) {
            dispatch(show(EditTreatmentModalName, {treatment: object.event}));
        } else {
            dispatch(show(EditGraftingModalName, {event: object.event}));
        }
    }, [dispatch, object]);

    if (!object.event) return null;
    return <i className="fas fa-edit pointer" onClick={onClick} />;
}

function RemoveComponent({animal, object}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const onRemoveClick = useCallback(() => {
        dispatch(
            show(ConfirmModalName, {
                title: t("removeEventForAnimalTitle", {animal: animal.AnmNo1}),
                text: t("removeEventForAnimalText", {animal: animal.AnmNo1}),
                confirmText: t("yes"),
                size: "lg",
                onConfirmed: (props) => {
                    return editSowCycle(
                        {
                            AnmID: animal.AnmID,
                            eventsToUpdate: [],
                            eventsToDelete: [object.event],
                        },
                        {FarmID: animal.FarmID}
                    )
                        .then((res) => {
                            props.handleHide();
                            editSowCycleNotificationSuccess(res);
                        })
                        .catch((e) => {
                            editSowCycleNotificationFailure(e);
                        });
                },
            })
        );
    }, [animal, dispatch, object, t]);

    if (!object.event) return null;
    return <i className="fas fa-trash pointer" onClick={onRemoveClick} />;
}

function GridRenderer({data, isGroup, animal, animals}) {
    const {t} = useTranslation();

    const mobile = useRef(isMobile());

    const graftingReasons = useSelector(
        (state) => state.dictionary.graftingReason.WData
    );
    const employees = useSelector(getEmployeeList);
    const service = useSelector(getServiceList);

    const dateValueFormatter = useCallback(
        (value) => {
            if (value) {
                if (value.Start && !moment(value.Start).isSame(value.End)) {
                    let text = moment(value.Start).format("L") + " - ";
                    if (value.End) {
                        text += moment(value.End).format("L");
                    } else {
                        text += "...";
                    }
                    return text;
                } else if (value.Date) {
                    return `${moment(value.Date).format("L")} (${
                        value.Delay < 0
                            ? t("cycleReasons.parturitionBeforeDays", {
                                  count: Math.abs(value.Delay),
                              })
                            : t("cycleReasons.parturitionAfterDays", {
                                  count: value.Delay,
                              })
                    })`;
                }
                return moment(value).format("L");
            }
            return "-";
        },
        [t]
    );

    const locationValueFormatter = useCallback(
        (AnmIDs) => {
            let locations = "";
            AnmIDs?.forEach((item) => {
                const animal =
                    animals.find((a) => a.AnmID === item) ||
                    animalsDB.getAnimalById(item, {
                        joinEvents: false,
                        findDeleted: true,
                    });
                let tempLoc = formatLocationName(
                    animal?.DelPlcmntID || animal?.PlcmntID
                );
                if (!locations.includes(tempLoc)) locations += tempLoc + " ";
            });
            return locations;
        },
        [animals]
    );

    const dayValueFormatter = useCallback(
        (value) => {
            const {
                graftingProgramReason,
                daysAfter,
                daysToGiveMedicine,
                daysReason,
                done,
            } = value;
            if (isNil(daysAfter)) return "-";
            let additionalText = "";
            switch (graftingProgramReason) {
                case GraftingProgramApplicationTypes.PARTURITION: {
                    additionalText = t(
                        "newSettings.breeding.graftingProgram.afterParturition"
                    ).toLowerCase();
                    break;
                }
                case GraftingProgramApplicationTypes.INSERTION: {
                    additionalText = t(
                        "newSettings.breeding.graftingProgram.afterInsertion"
                    ).toLowerCase();
                    break;
                }
                case GraftingProgramApplicationTypes.AGE: {
                    additionalText = t(
                        "newSettings.breeding.graftingProgram.afterBirth"
                    ).toLowerCase();
                    break;
                }
                case GraftingProgramApplicationTypes.SEPARATION: {
                    additionalText = t(
                        "newSettings.breeding.graftingProgram.afterSeparation"
                    ).toLowerCase();
                    break;
                }
                case GraftingProgramApplicationTypes.INSEMINATION: {
                    additionalText = t(
                        "newSettings.breeding.graftingProgram.afterInsemination"
                    ).toLowerCase();
                    break;
                }
                default: {
                    switch (daysReason) {
                        case GraftingProgramApplicationTypes.INSERTION: {
                            additionalText = t(
                                "newSettings.breeding.graftingProgram.afterInsertion"
                            ).toLowerCase();
                            break;
                        }
                        case GraftingProgramApplicationTypes.AGE: {
                            additionalText = t(
                                "newSettings.breeding.graftingProgram.afterBirth"
                            ).toLowerCase();
                            break;
                        }
                        case GraftingProgramApplicationTypes.INSEMINATION: {
                            additionalText = t(
                                "newSettings.breeding.graftingProgram.afterInsemination"
                            ).toLowerCase();
                            break;
                        }
                        default: {
                            !isGroup && !animal.AnimalKind
                                ? (additionalText = t(
                                      "newSettings.breeding.graftingProgram.afterInsemination"
                                  ).toLowerCase())
                                : (additionalText = t(
                                      "newSettings.breeding.graftingProgram.afterBirth"
                                  ).toLowerCase());
                            break;
                        }
                    }
                    break;
                }
            }
            if (daysToGiveMedicine > 1 && done !== 1)
                return `${daysAfter} - ${daysAfter + daysToGiveMedicine - 1} ${additionalText}`;
            return `${daysAfter} ${additionalText}`;
        },
        [animal?.AnimalKind, isGroup, t]
    );

    const medicineValueFormatter = useCallback((value) => {
        if (value) return value.WData.Name;
        else return "-";
    }, []);

    const withdrawalPeriodFormatter = useCallback((value) => {
        if (!value.medicine?.WData?.GracePeriod || !value.event) return null;
        const date = moment(value.date)
            .startOf("day")
            .add(value.medicine.WData.GracePeriod, "days");
        if (!date.isAfter(new Date())) return null;
        return date.toNow(true);
    }, []);

    const reasonValueFormatter = useCallback(
        (value) => {
            let reason = graftingReasons.find((reason) => reason.ID === value);
            if (reason) return reason.Value;
        },
        [graftingReasons]
    );

    const treatmentTypeFormatter = useCallback((value) => {
        if (!value) return "";
        return treatmentTranslator(value);
    }, []);

    const employeesFormatter = useCallback(
        (value) => {
            if (typeof value === "string")
                return employeesValueFormatter(value, employees, service);
            else if (isNil(value)) return "";
            else
                return value.map(
                    (item) =>
                        employeesValueFormatter(item, employees, service) + " "
                );
        },
        [employees, service]
    );

    const headers = useMemo(() => {
        const tmp = [];
        if (isGroup) {
            tmp.push({
                name: t("location"),
                field: "_AnmIDs",
                valueFormatter: locationValueFormatter,
            });
        }
        if (isGroup || animal.AnmCnt > 1) {
            tmp.push({
                name: t("animalCount"),
                field: "animalsCnt",
            });
        }
        tmp.push(
            {
                name: t("date"),
                field: "date",
                valueFormatter: dateValueFormatter,
                customSort: sortDateStrings,
                _mobileDate: true,
            },
            {
                name: t("day"),
                headerComponent: (
                    <DateHeaderComponent
                        animal={animal}
                        showFromInsemination={true}
                    />
                ),
                valueFormatter: dayValueFormatter,
            },
            {
                name: t("medicine"),
                field: "medicine",
                valueFormatter: medicineValueFormatter,
                colWidth: 2,
            },
            {
                name: t("givenDose"),
                field: "dose",
            },
            {
                name: t("modals.medicineModal.gracePeriod"),
                valueFormatter: withdrawalPeriodFormatter,
            },
            {
                name: t("reason"),
                field: "reason",
                valueFormatter: reasonValueFormatter,
            },
            {
                name: t("grid.treatmentType"),
                field: "medicine.WData.Type",
                valueFormatter: treatmentTypeFormatter,
            },
            {
                name: t("done"),
                field: "done",
                headerComponent: <DoneHeaderComponent showDoneTooltip={true} />,
                component: (props) => <IconComponent {...props} />,
            },
            {
                name: t("eventGrid.operator"),
                field: "operator",
                valueFormatter: employeesFormatter,
                shouldShow: () => checkIfUserHasPrivilegedAccess(),
                colWidth: 2,
            },
            {
                name: t("comment"),
                field: "comment",
            }
        );
        if (!isGroup) {
            tmp.push(
                {
                    name: "",
                    headerClassName: "index",
                    itemClassName: "index",
                    notSortable: true,
                    component: (props) => <EditComponent {...props} />,
                },
                {
                    name: "",
                    headerClassName: "index",
                    itemClassName: "index",
                    notSortable: true,
                    component: (props) => (
                        <RemoveComponent {...props} animal={animal} />
                    ),
                }
            );
        }
        return tmp;
    }, [
        t,
        animal,
        dateValueFormatter,
        isGroup,
        locationValueFormatter,
        dayValueFormatter,
        medicineValueFormatter,
        reasonValueFormatter,
        withdrawalPeriodFormatter,
        employeesFormatter,
        treatmentTypeFormatter,
    ]);

    return (
        <TableGrid
            data={data}
            headers={headers}
            mobileRow={<DefaultMobileRow />}
            showPagination={!mobile.current}
            paginationItems={isGroup ? 50 : 10}
            scrollOnPageChange={false}
        />
    );
}

function GroupGraftingProgramGridCard({showCard, animal}) {
    const getAnimals = useMemo(makeGetAnimalsInGroup, []);

    const data = useSelector(getMedicineArrayForGroup);
    const animals = useSelector(getAnimals);

    return (
        <GraftingProgramGridCardWrapper
            showCard={showCard}
            defaultExpanded={data.length > 0}>
            <GridRenderer
                data={data}
                isGroup={true}
                animal={animal}
                animals={animals}
            />
        </GraftingProgramGridCardWrapper>
    );
}

function AnimalGraftingProgramGridCard({showCard, cycle, animal}) {
    const medicineArrayParameters = useMemo(
        () => ({
            AnimalKind: animal?.AnimalKind,
            cycleNumber: cycle,
        }),
        [animal?.AnimalKind, cycle]
    );

    const data = useSelector((state) =>
        getMedicineArrayForSelectedAnimal(state, medicineArrayParameters)
    );

    return (
        <GraftingProgramGridCardWrapper
            showCard={showCard}
            defaultExpanded={data.length > 0}>
            <GridRenderer data={data} isGroup={false} animal={animal} />
        </GraftingProgramGridCardWrapper>
    );
}

export default function GraftingProgramGridCard({
    showCard = true,
    isGroup,
    cycle,
}) {
    const animal = useSelector(getSelectedAnimalForDocuments);

    if (isGroup)
        return (
            <GroupGraftingProgramGridCard showCard={showCard} animal={animal} />
        );
    return (
        <AnimalGraftingProgramGridCard
            showCard={showCard}
            cycle={cycle}
            animal={animal}
        />
    );
}
